import { type SpaceMouseStatus } from "./status";
import { type LoopbackPermissionState } from "./useLoopbackPermission";

/**
 * Which single note belongs under the SpaceMouse toggle, or null for none.
 *
 * Ordered most specific first, because more than one can be true at once and only the narrowest is worth
 * saying:
 *
 * - `denied` wins outright and shows even with the setting off, since a denial is what switched it off.
 * - An unanswered prompt outranks any driver complaint. While the browser is still asking, the request
 *   never left it, so we genuinely cannot know whether the driver is there — and "install 3DxWare" would
 *   send someone off to fix the wrong thing. This note stays until the permission resolves one way or the
 *   other, rather than until a connection succeeds: a dismissed prompt leaves the state at "prompt"
 *   indefinitely, and that's exactly when the user most needs telling.
 * - Only once the permission is settled is the driver worth blaming.
 */
export function spaceMouseNoteKey(
    permission: LoopbackPermissionState,
    enabled: boolean,
    status: SpaceMouseStatus,
): string | null {
    if (permission === "denied") {
        return "spaceMouse.blocked";
    }
    if (!enabled) {
        return null;
    }
    if (permission === "prompt") {
        return "spaceMouse.awaitingPermission";
    }
    return status === "unavailable" ? "spaceMouse.unavailable" : null;
}

/** Warning while the browser is still asking; error once the driver or the permission is the problem. */
export function spaceMouseNoteSeverity(key: string): "warning" | "error" {
    return key === "spaceMouse.awaitingPermission" ? "warning" : "error";
}
